"use client";
import * as dc from "@/components/layout/list/defaultType";
import { useFormatter, useTranslations } from "next-intl";
import { Skeleton } from "@radix-ui/themes";
import { DataInputParams } from "./inputType";
import { Form, InputNumber, Skeleton as AntSkeleton, FormInstance, Button, Tooltip, Flex, Space } from "antd";
import { Debug } from "@/util/debug";
import { checkDisabled, checkNoItem, GetDataInputElement, getPlaceholder, getTooltip, UnitConvertor } from "./inputFunction";
import { ClearButton, UnitSelect } from "./inputButton";
import SkeletonInput from "antd/es/skeleton/Input";
import { TransitionType } from "@/components/layout/layoutType";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { checkEmpty, checkEmptyValue } from "@/util/stringutil";

// import Icon from "@/components/common/icon/icon";
// import dynamic from "next/dynamic";
// const Icon = dynamic(() => import("@/components/common/icon/icon"), { ssr: false });

export const getInput = (t: TransitionType, params: DataInputParams) => {
  const disabled = checkDisabled(params);
  const placeholder = getPlaceholder(t, params, disabled) ?? params.value ?? params.defaultValue;
  const value = checkEmptyValue(params.inputMode == "view" && !(params.readInputDisabled ?? false) ? params.value : undefined) ?? "";
  const unitPrev =
    params.unitPrev && ((params.emptyUnit ?? true) || value !== undefined)
      ? GetDataInputElement(params.unitPrev, false, params.values, t)
      : undefined;
  const unit =
    params.unit && ((params.emptyUnit ?? true) || value !== undefined)
      ? GetDataInputElement(params.unit, true, params.values, t)
      : undefined;
  //Debug(params);
  //const disabled = params.inputMode == "read" ? true : params.disabled;
  const step = params.step ?? (params.scale ? 1 / params.scale : undefined);
  const onChange = params.onChange
    ? (e: any) => {
        if (params.onChange) {
          //Debug(params.onChange);
          if (params.form && params.nullValue !== undefined && e === params.nullValue) {
            params.form.setFieldValue(params.name, null);
          }
          params.onChange({ key: params.name, value: e, index: params.index, name: params.subListName });
        }
      }
    : undefined;
  return (
    <InputNumber
      className={cn(
        params.inputClassName,
        params.hiddenCheckValue ? "item-hidden-check" : "",
        params.nextInputHidden ? "next-input-hidden" : "",
      )}
      type={params.type}
      style={params.inputStyle}
      variant={params.variant ?? (params.inputMode === "view" ? dc.viewVariant : undefined)}
      placeholder={placeholder}
      value={value}
      //defaultValue={params.value}
      disabled={disabled}
      prefix={unitPrev}
      suffix={unit}
      id={params.name}
      name={params.name}
      min={params.minValue !== undefined ? Number(params.minValue) : undefined}
      max={params.maxValue !== undefined ? Number(params.maxValue) : undefined}
      minLength={params.minLength}
      maxLength={params.maxLength}
      step={step}
      // onStep={(value: number, info: { offset: any; type: "up" | "down" }) => {
      //   Debug(value, info);
      // }}
      formatter={params.formatter}
      parser={params.parser}
      onChange={onChange}
      changeOnWheel={false}
      onWheel={(e) => {
        e.currentTarget.blur();
        // e.stopPropagation();
      }}
      readOnly={params.readOnly === undefined ? disabled : params.readOnly}
      onPressEnter={params.onPressEnter}
      autoComplete={params.autocomplete ? "on" : params.autocomplete === undefined ? "on" : "off"}
    />
  );
};

export default function DataInputNumber(params: DataInputParams) {
  const t = useTranslations(params.transitionGroup ?? "contents");
  const [tooltip, setTooltip] = useState(getTooltip(t, params));
  const [unit, setUnit] = useState(params.unit);
  const [step, setStep] = useState(params.step ?? (params.scale ? 1 / params.scale : undefined));
  const [scale, setScale] = useState(params.scale);
  const prev = params.prev ? GetDataInputElement(params.prev, false, params.values, t) : undefined;
  const next = params.next ? GetDataInputElement(params.next, true, params.values, t) : undefined;

  if (params.formFunction && params.name) {
    if (!params.formFunction[params.name]) {
      params.formFunction[params.name] = {};
    }
    params.formFunction[params.name]["setUnit"] = setUnit;
    params.formFunction[params.name]["setStep"] = setStep;
  }
  if (params.formMemo && params.name) {
    if (!params.formMemo[params.name]) {
      params.formMemo[params.name] = {};
      const temp_memo = params.formMemo[params.name];
      if (temp_memo) {
        const beforeUnit = temp_memo.convertUnit ?? params.unit;
        const beforeScale = temp_memo.convertScale ?? params.scale;
        const beforeStep = temp_memo.convertStep ?? params.step ?? (params.scale ? 1 / params.scale : undefined);
        params.formMemo[params.name] = {
          convertUnit: unit,
          convertScale: beforeScale,
          convertStep: beforeStep,
        };
        // UnitConvertor(params);
      }
    }
  }

  useEffect(() => {
    setTooltip(getTooltip(t, params));
  }, [t, params, params.tooltip]);

  useEffect(() => {
    if (params.formMemo && params.name) {
      if (!params.formMemo[params.name]) {
        params.formMemo[params.name] = {};
      }
      const temp_memo = params.formMemo[params.name];
      if (temp_memo) {
        const beforeUnit = temp_memo.convertUnit ?? params.unit;
        const beforeScale = temp_memo.convertScale ?? params.scale;
        const beforeStep = temp_memo.convertStep ?? params.step ?? (params.scale ? 1 / params.scale : undefined);
        params.formMemo[params.name] = {
          ...params.formMemo[params.name],
          beforeUnit,
          beforeScale,
          beforeStep,
          convertUnit: unit,
          convertScale: beforeScale,
          convertStep: beforeStep,
        };
        UnitConvertor(params);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.formMemo, params.name, params.unit, params.step, params.scale, unit]);
  //Debug(params.name, tooltip);
  return (
    <Tooltip color={dc.tooltipColor} title={tooltip} placement={params.tooltipPlacement} {...params.tooltipProps}>
      <Flex>
        {params.clearProp?.clearButton && (params.clearProp?.clearButtonFront ?? false) ? <ClearButton {...params} /> : ""}
        <Space.Compact className={cn(params.spaceClassName)}>
          {prev}
          {checkNoItem(params) ? (
            getInput(t, { ...params, unit: unit })
          ) : (
            <Form.Item name={params.name} noStyle initialValue={params.value ?? params.defaultValue} required={params.required}>
              {getInput(t, { ...params, unit: unit })}
            </Form.Item>
          )}
          {next}
        </Space.Compact>
        <UnitSelect {...params} />
        {params.clearProp?.clearButton && !(params.clearProp?.clearButtonFront ?? false) ? <ClearButton {...params} /> : ""}
      </Flex>
    </Tooltip>
  );
}
